/**
 * YEEFINE Header — mobile search toggle & navigation drawer.
 *
 * Handles:
 *  - Mobile search icon expanding/collapsing the search form.
 *  - Hamburger button opening the mobile navigation drawer.
 *  - Drawer close via overlay click, close button, or the ESC key.
 *  - Body scroll locking while the drawer is open.
 *  - Accordion expand/collapse for nested menu items.
 */
import { lockScroll, unlockScroll } from '@theme/utilities';

const DESKTOP_BREAKPOINT = 1024;

function closeSearchPanel(toggle, panel) {
  if (!toggle || !panel) return;
  panel.hidden = true;
  toggle.setAttribute('aria-expanded', 'false');
}

function openSearchPanel(toggle, panel) {
  if (!toggle || !panel) return;
  panel.hidden = false;
  toggle.setAttribute('aria-expanded', 'true');
  const input = panel.querySelector('[data-yeefine-search-input]');
  if (input) {
    requestAnimationFrame(() => input.focus());
  }
}

function initSearchToggle(header) {
  const toggle = header.querySelector('[data-yeefine-search-toggle]');
  const panel = header.querySelector('[data-yeefine-search-panel]');
  if (!toggle || !panel) return;

  toggle.addEventListener('click', () => {
    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
    if (isOpen) {
      closeSearchPanel(toggle, panel);
    } else {
      openSearchPanel(toggle, panel);
    }
  });

  document.addEventListener('click', (event) => {
    if (panel.hidden) return;
    if (panel.contains(event.target) || toggle.contains(event.target)) return;
    closeSearchPanel(toggle, panel);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !panel.hidden) {
      closeSearchPanel(toggle, panel);
      toggle.focus();
    }
  });
}

function initDrawer(header) {
  const toggle = header.querySelector('[data-yeefine-drawer-toggle]');
  const drawer = header.querySelector('[data-yeefine-drawer]');
  if (!toggle || !drawer) return;

  const closeElements = drawer.querySelectorAll('[data-yeefine-drawer-close]');
  const panel = drawer.querySelector('.yeefine-drawer__panel');

  function openDrawer() {
    const logo = drawer.querySelector('template[data-yf-drawer-logo]');
    if (logo) logo.replaceWith(logo.content.cloneNode(true));
    drawer.setAttribute('data-open', '');
    drawer.setAttribute('aria-hidden', 'false');
    toggle.setAttribute('aria-expanded', 'true');
    lockScroll(drawer);
    if (panel) panel.focus({ preventScroll: true });
  }

  function closeDrawer() {
    drawer.removeAttribute('data-open');
    drawer.setAttribute('aria-hidden', 'true');
    toggle.setAttribute('aria-expanded', 'false');
    unlockScroll(drawer);
    toggle.focus();
  }

  toggle.addEventListener('click', () => {
    const isOpen = drawer.hasAttribute('data-open');
    if (isOpen) {
      closeDrawer();
    } else {
      openDrawer();
    }
  });

  closeElements.forEach((element) => {
    element.addEventListener('click', closeDrawer);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && drawer.hasAttribute('data-open')) {
      closeDrawer();
    }
  });

  window.addEventListener('resize', () => {
    if (window.innerWidth >= DESKTOP_BREAKPOINT && drawer.hasAttribute('data-open')) {
      closeDrawer();
    }
  });
}

function initAccordion(header) {
  const toggles = header.querySelectorAll('[data-yeefine-accordion-toggle]');

  toggles.forEach((toggle) => {
    const panel = toggle.nextElementSibling;
    if (!panel || !panel.matches('[data-yeefine-accordion-panel]')) return;

    toggle.addEventListener('click', () => {
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!isOpen));
      if (!isOpen) {
        panel.querySelectorAll('template[data-yf-menu-image]').forEach(image =>
          image.replaceWith(image.content.cloneNode(true)));
      }
      panel.classList.toggle('is-open', !isOpen);
    });
  });
}

document.querySelectorAll('.yeefine-header').forEach((header) => {
  initSearchToggle(header);
  initDrawer(header);
  initAccordion(header);
});
